import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	type RefObject,
} from 'react';
import throttleFnc from 'lodash/throttle';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer';
import type { ScrollElementContainerProps, Handler, Options, Selector } from './types';

const DEFAULT_OPTIONS = {
	throttle: true,
	wait: 150,
};

const ScrollElementContext = createContext<RefObject<HTMLElement>>({ current: null });

ScrollElementContext.displayName = 'ScrollElementContext';

export function useScrollElement(): RefObject<HTMLElement> {
	const scrollElement = useContext(ScrollElementContext);

	return scrollElement;
}

export function ScrollElementProvider({ scrollRef, children }: ScrollElementContainerProps) {
	return (
		<ScrollElementContext.Provider value={scrollRef}>{children}</ScrollElementContext.Provider>
	);
}

/**
 * Will store in local state the value returned by the selector.
 *
 * @param selector - derive value/values from scroll element which will be updated on subsequent `scroll` and `resize` events.
 * @param options.throttle - set to `false` to disable throttling of callback functions. Default `true`.
 * @param options.wait - set the delay in milliseconds applied to the throttled functions. Default `150`.
 * @returns State in the shape set by the selector.
 */
export function useComputedScrollState<T>(selector: Selector<T>, options?: Options) {
	const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
	const scrollElement = useScrollElement();
	const [scrollState, setScrollState] = useState<T>(() => selector(scrollElement.current));

	useLayoutEffect(() => {
		if (!scrollElement.current) return;

		setScrollState(() => {
			return selector(scrollElement.current);
		});
	}, [scrollElement, selector]);

	const handler = useCallback(
		(scrollContainer: HTMLElement) => {
			setScrollState(() => {
				return selector(scrollContainer);
			});
		},
		[selector],
	);

	useScrollEvent(handler, mergedOptions);
	useResizeEvent(handler, mergedOptions);

	return scrollState;
}

function useScrollListener(handler: Handler, element: RefObject<HTMLElement>, options: Options) {
	const { throttle, wait } = options;
	const callback = useThrottle(handler, options);

	const updater = useCallback(
		(scrollElement: HTMLElement) => {
			callback(scrollElement);
		},
		[callback],
	);

	useEffect(() => {
		const targetElement = element?.current;

		if (!targetElement?.addEventListener) return;

		const listener = () => {
			updater(targetElement);
		};

		targetElement.addEventListener('scroll', listener);

		return () => {
			targetElement.removeEventListener('scroll', listener);
		};
	}, [updater, throttle, wait, element]);
}

function useScrollEvent(handler: Handler, options: Options) {
	const element = useScrollElement();
	useScrollListener(handler, element, options);
}

function useResizeEvent(handler: Handler, options: Options) {
	const element = useScrollElement();
	const callback = useThrottle(handler, options);

	const handleResize = useCallback(() => {
		element.current && callback(element.current);
	}, [callback, element]);

	useResizeObserver({
		ref: element,
		onResize: handleResize,
	});
}

function useThrottle(handler: Handler, options: Options) {
	const { throttle, wait } = options;

	const callback = useMemo(
		() => (throttle ? throttleFnc(handler, wait) : handler),
		[handler, throttle, wait],
	);

	return callback;
}
